import { Extension, Editor } from '@tiptap/core';
import { nanoid } from 'nanoid';
import { Plugin as StatePlugin, PluginKey as StatePluginKey, Transaction } from '@tiptap/pm/state';
import { Node } from '@tiptap/pm/model';

export const ATTRIBUTE_NAME = 'data-toc-id';

let updateTimeout: any = null;
export type GetIdFunction = (arg0?: any) => string;

export type TableOfContentPluginOptions = {
  getId: GetIdFunction;
  headingType?: string;
};

export interface TocItem {
  id: string;
  isActive?: boolean;
  isScrolledOver?: boolean;
  [key: string]: any; // For any additional properties
}

export interface Storage {
  content: any[];
  headlines: any[];
  scrollHandler: () => void;
  scrollPosition: number;
  parent: HTMLElement | null;
}

export interface Heading {
  node: any;
  pos: number;
}

export const createStorage = (): Storage => ({
  content: [],
  headlines: [],
  scrollHandler: () => null,
  scrollPosition: 0,
  parent: null,
});

export const processHeadingNodes = (
  doc: any,
  headingType: string,
  getId: GetIdFunction,
  tr: Transaction
) => {
  let modified = false;
  const existingIds: string[] = [];
  doc.descendants((node: Node, pos: number) => {
    if (node.type.name === headingType) {
      const tocId = node.attrs[ATTRIBUTE_NAME];
      if (tocId == null || existingIds.includes(tocId)) {
        const newId = getId ? getId(node.textContent) : nanoid(8);
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, [ATTRIBUTE_NAME]: newId, id: newId });
        modified = true;
      }
      existingIds.push(tocId);
    }
  });
  return modified;
};

export const createTableOfContentPlugin = ({
  getId,
  headingType = 'heading',
}: TableOfContentPluginOptions) =>
  new StatePlugin({
    key: new StatePluginKey('tableOfContent'),
    appendTransaction(transactions, oldState, newState) {
      const tr = newState.tr;
      let modified = false;
      if (transactions.some((transaction) => transaction.docChanged)) {
        const existingIds: string[] = [];
        newState.doc.descendants((node: Node, pos: number) => {
          if (node.type.name === (headingType || 'heading')) {
            const tocId = node.attrs[ATTRIBUTE_NAME];
            if (tocId == null || existingIds.includes(tocId)) {
              const newId = getId?.(node.textContent) ?? nanoid(8);
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                [ATTRIBUTE_NAME]: newId,
                id: newId,
              });
              modified = true;
            }
            existingIds.push(tocId);
          }
        });
      }

      return modified ? tr : null;
    },
  });

export interface MapTableOfContentParams {
  editor: Editor;
  headingType?: string;
  storage: Storage;
  onUpdate?: (tocItems: TocItem[], isEmpty: boolean) => void;
}

export const mapTocItems = (
  tocItems: TocItem[],
  { editor, headingType = 'heading', storage, onUpdate }: MapTableOfContentParams
) => {
  const headings: Heading[] = [];
  const scrolledOverIds: string[] = [];
  let activeId: string | null = null;

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === headingType) {
      headings.push({ node, pos });
    }
  });

  headings.forEach(({ node, pos }) => {
    let domNode: HTMLElement | null = null;
    if ((editor.view as any)?.docView) {
      domNode = editor.view.domAtPos(pos + 1).node as HTMLElement;
    }
    if (domNode && storage.scrollPosition >= domNode?.offsetTop) {
      activeId = node.attrs[ATTRIBUTE_NAME];
      scrolledOverIds.push(node.attrs[ATTRIBUTE_NAME]);
    }
  });

  tocItems = tocItems.map((item) => ({
    ...item,
    isActive: item.id === activeId,
    isScrolledOver: scrolledOverIds.includes(item.id),
  }));

  if (onUpdate) {
    const isEmpty = storage.content.length === 0;
    onUpdate(tocItems, isEmpty);
  }

  return tocItems;
};

export interface UpdateTocParams {
  editor: Editor;
  onUpdate?: (tocItems: any[], isEmpty: boolean) => void;
  headingType?: string;
  storage: Storage;
  linearIndexes?: boolean;
}

const updateTableOfContents = ({
  editor,
  onUpdate,
  headingType = 'heading',
  storage,
  linearIndexes,
}: UpdateTocParams) => {
  const headings: Heading[] = [];
  let tocItems: TocItem[] = [];
  const domNodes: HTMLElement[] = [];

  // Collect all headings in the document
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === headingType) {
      headings.push({ node, pos });
    }
  });

  // Process each heading
  headings.forEach((heading, index) => {
    let domNode: HTMLElement | null = null;
    if ((editor.view as any)?.docView) {
      domNode = editor.view.domAtPos(heading.pos + 1).node as HTMLElement;
    }
    let isScrolledOver = false;
    if (domNode) {
      isScrolledOver = storage.scrollPosition >= domNode.offsetTop;
      domNodes.push(domNode);
    }

    const { level: originalLevel, textContent } = heading.node.attrs;
    const id = heading.node.attrs[ATTRIBUTE_NAME] ?? heading.node.attrs.id;
    let level = 1;
    let itemIndex = 1;

    if (!headings[index - 1]) {
      tocItems.push({
        itemIndex,
        id,
        originalLevel,
        level,
        textContent,
        pos: heading.pos,
        editor,
        isActive: false,
        isScrolledOver,
        node: heading.node,
        dom: domNode,
      });
      return;
    }

    const lastItem = tocItems.at(-1);
    const previousItemWithSameOrHigherLevel = [...tocItems]
      .reverse()
      .find((item) => item.originalLevel <= originalLevel);
    const previousLevel = previousItemWithSameOrHigherLevel?.level || 1;

    if (originalLevel > (lastItem?.originalLevel || 1)) {
      level = (lastItem?.level || 1) + 1;
    } else if (originalLevel < (lastItem?.originalLevel || 1)) {
      level = previousLevel;
    } else {
      level = lastItem?.level || 1;
    }

    if (linearIndexes) {
      itemIndex = (lastItem?.itemIndex || 1) + 1;
    } else {
      const itemsWithSameOrHigherLevel = tocItems.filter((item) => item.level <= level);
      itemIndex = (itemsWithSameOrHigherLevel.at(-1)?.itemIndex || 1) + 1;
    }

    tocItems.push({
      itemIndex,
      id,
      originalLevel,
      level,
      textContent,
      pos: heading.pos,
      editor,
      isActive: false,
      isScrolledOver,
      node: heading.node,
      dom: domNode,
    });
  });

  tocItems = mapTocItems(tocItems, { editor, onUpdate, headingType, storage });

  if (onUpdate) {
    const isEmpty = storage.content.length === 0;
    onUpdate(tocItems, isEmpty);
  }

  storage.headlines = domNodes;
  storage.content = tocItems;
  editor.state.tr.setMeta('toc', tocItems);
  editor.view.dispatch(editor.state.tr);
};

const TableOfContent = Extension.create({
  name: 'tableOfContent',

  addStorage: createStorage,

  addGlobalAttributes() {
    return [
      {
        types: [this.options.headingType || 'heading'],
        attributes: {
          id: {
            default: null,
            renderHTML: (el) => ({ id: el.id }),
            parseHTML: (el) => el.id ?? null,
          },
          [ATTRIBUTE_NAME]: {
            default: null,
            renderHTML: (el) => ({ [ATTRIBUTE_NAME]: el[ATTRIBUTE_NAME] }),
            parseHTML: (el) => el.dataset.tocId ?? null,
          },
        },
      },
    ];
  },

  addOptions() {
    return {
      onUpdate: () => {},
      getId: () => nanoid(8),
      linearIndexes: !1,
      headingType: 'heading',
      scrollParentClass: '.__panel',
      updateDelay: 100,
    };
  },
  onUpdate() {
    clearTimeout(updateTimeout);
    updateTimeout = setTimeout(() => {
      updateTableOfContents({
        editor: this.editor,
        storage: this.storage,
        onUpdate: this.options.onUpdate?.bind(this),
        linearIndexes: this.options.linearIndexes,
        headingType: this.options.headingType,
      });
    }, this.options.updateDelay);
  },
  onDestroy() {
    // Remove the scroll event listener
    if (this.storage.parent) {
      this.storage.parent?.removeEventListener?.('scroll', this.storage.scrollHandler);
    }
  },
  onCreate() {
    const { tr } = this.editor.state;
    const existingIds: string[] = [];

    // Collect all headings in the document and assign unique IDs
    this.editor.state.doc.descendants((node, pos) => {
      if (node.type.name === this.options.headingType) {
        const tocId = node.attrs[ATTRIBUTE_NAME];
        if (tocId == null || existingIds.includes(tocId)) {
          const newId = this.options.getId ? this.options.getId(node.textContent) : nanoid(8);
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, [ATTRIBUTE_NAME]: newId, id: newId });
        }
        existingIds.push(tocId);
      }
    });

    // Dispatch the transaction
    this.editor.view.dispatch(tr);

    // Update the table of contents
    updateTableOfContents({
      editor: this.editor,
      storage: this.storage,
      onUpdate: this.options.onUpdate?.(),
      linearIndexes: this.options.linearIndexes,
      headingType: this.options.headingType,
    });
    // Set up the scroll handler
    this.storage.scrollHandler = () => {
      let scrollPosition;
      const scrollParent = this.storage.parent;

      if (scrollParent instanceof HTMLElement) {
        scrollPosition = scrollParent.scrollTop;
      }

      this.storage.scrollPosition = scrollPosition ?? 0;

      this.storage.content = mapTocItems(this.storage.content, {
        editor: this.editor,
        headingType: this.options.headingType,
        storage: this.storage,
        onUpdate: this.options.onUpdate?.bind(this),
      });
    };

    if (!this.storage.parent) {
      this.storage.parent = this.editor.view.dom?.closest(this.options.scrollParentClass);
      this.storage.parent?.addEventListener?.('scroll', this.storage.scrollHandler);
    }
  },
  addProseMirrorPlugins() {
    return [
      createTableOfContentPlugin({
        getId: this.options.getId,
        headingType: this.options.headingType,
      }),
    ];
  },
});

export {
  TableOfContent,
  createTableOfContentPlugin as TableOfContentPlugin,
  TableOfContent as default,
};
